export default class LayerManager {
  constructor({ hook, map }) {
    this.featureStateRevisions = {}
    this.loadedSources = {}
    this.renderingLayers = {}

    this.map = map
      .on('sourcedata', (e) => {
        // Note: this checking might need to be different in later versions of MapboxGL
        // Also consider moving to LibreMap: https://github.com/maplibre/maplibre-gl-js/tree/main
        if (
          this.loadedSources[e.sourceId] == false &&
          e.isSourceLoaded &&
          e.sourceDataType == 'metadata'
        ) {
          this.loadedSources[e.sourceId] = true
          hook.sendEvent('map.source_loaded', { sourceId: e.sourceId })
        }
      })
      .on('idle', () => {
        // PDI: this event might not be needed anymore. Would be great to remove it as it's really noisy and does
        // trigger before the "last batch" is received so isn't that reliable.
        hook.sendEvent('map.idle', {})

        const layers = Object.values(this.renderingLayers)
        const stillRendering = layers.some((lastBatch) => !lastBatch)

        if (layers.length > 0 && !stillRendering) {
          hook.sendEvent('map.layer_rendering_completed', this.renderingLayers)
          this.renderingLayers = {}
        }
      })

    hook.handleTargetedEvent('set-sources', ({ sources }) =>
      ensureArray(sources).forEach((config) => this.setSource(config))
    )

    hook.handleTargetedEvent('set-layers', ({ category, layers }) =>
      ensureArray(layers).forEach((config) => {
        this.setLayer(config, category)
        hook.sendEvent('map.layer_added', config.id)
      })
    )

    hook.handleTargetedEvent('show-layers', ({ layer_ids }) => {
      layer_ids.forEach((layer_id) =>
        this.map.setLayoutProperty(layer_id, 'visibility', 'visible')
      )
      hook.sendEvent('map.layers_added', layer_ids)
    })

    hook.handleTargetedEvent('blink-layers', ({ layer_ids }) => {
      layer_ids.forEach((layer_id) =>
        this.map.setLayoutProperty(layer_id, 'visibility', 'visible')
      )
      window.setTimeout(
        () =>
          layer_ids.forEach((layer_id) =>
            this.map.setLayoutProperty(layer_id, 'visibility', 'none')
          ),
        500
      )
      window.setTimeout(
        () =>
          layer_ids.forEach((layer_id) =>
            this.map.setLayoutProperty(layer_id, 'visibility', 'visible')
          ),
        1000
      )
      window.setTimeout(
        () =>
          layer_ids.forEach((layer_id) =>
            this.map.setLayoutProperty(layer_id, 'visibility', 'none')
          ),
        1500
      )
    })

    hook.handleTargetedEvent('hide-layers', ({ layer_ids }) => {
      const existing = layer_ids.filter((id) => this.map.getLayer(id))

      existing.forEach((id) => {
        this.map.setLayoutProperty(id, 'visibility', 'none')
      })
    })

    hook.handleTargetedEvent(
      'update-feature-state',
      async ({
        key,
        field,
        value,
        polygon_ids,
        revision,
        source,
        sourceLayer,
        last_batch
      }) => {
        if (revision < this.featureStateRevisions[field]) return

        // the value becomes the "source" once the last batch has been processed indicating it's finished
        this.renderingLayers[key] = false

        this.featureStateRevisions[field] = revision

        polygon_ids.forEach((polygon_id) => {
          this.map = this.map.setFeatureState(
            {
              id: polygon_id,
              source: source,
              sourceLayer: sourceLayer
            },
            { [field]: value }
          )
        })

        if (last_batch) {
          hook.sendEvent('map.last_batch_completed', { [key]: source })
          this.renderingLayers[key] = source
        }
      }
    )

    hook.handleTargetedEvent(
      'clear-feature-state',
      async ({ field, source, sourceLayer }) => {
        // IMPORTANT: querySourceFeatures only returns visible features within the viewed bounding area so this only
        // works when the layer is visible and only for whats on screen. This means can't hide a layer than clear the
        // features that were being shown
        // Source: https://github.com/mapbox/mapbox-gl-js/issues/2481#issuecomment-1198225913
        this.map
          .querySourceFeatures(source, {
            sourceLayer: sourceLayer
          })
          .forEach(({ id }) => {
            // need this check as from NTW testing the source from mapbox _can_ contain features with no id
            // this might be due to data quality and something we can fix at a later point when we generate thoese tilesets
            //
            // PDI: comment from Chris in a review of this:
            // https://github.com/Vibrant-Planet/landtender/pull/697#discussion_r932722084
            // so, mapbox can split features across tiles if they overlap a boundary. that means that in any given map there
            // may be multiple polygons that make up a single feature. i think it may alter the feature ID in this case. you
            // may notice "shearing" within a single feature, especially if you zoom in more. the best way to fix this is to
            // use a value that's not the feature ID, unfortunately. apologies for not remembering this detail sooner!
            if (Number.isInteger(id)) {
              this.map = this.map.setFeatureState(
                {
                  id: id,
                  source: source,
                  sourceLayer: sourceLayer
                },
                { [field]: null }
              )
            }
          })
      }
    )

    // debugging events

    hook.handleTargetedEvent('debug/change-basemap', ({ styleId }) => {
      // NTW: inspired by: https://github.com/mapbox/mapbox-gl-js/issues/4006#issuecomment-772462907
      // What it does: when changing the style used all sources and layers have to be reloaded against the new style otherwise
      // they are lost. This script copies over any already defined to the new mapbox style. It does not retain feature state,
      // ie the currently shown stuff, maybe something hacky like this might help:
      // https://github.com/mapbox/mapbox-gl-js/issues/4006#issuecomment-994559303

      fetch(
        `https://api.mapbox.com/styles/v1/vibrantplanet/${styleId}?access_token=${land_tender.mapbox.public_token}`
      )
        .then((response) => response.json())
        .then((newStyle) => {
          const currentStyle = this.map.getStyle()
          // ensure any sources from the current style are copied across to the new style
          newStyle.sources = Object.assign(
            {},
            currentStyle.sources,
            newStyle.sources
          )

          // filter our Mapbox layers while keeping ours
          const appLayers = currentStyle.layers.filter((el) => {
            return (
              el.source &&
              el.source != 'mapbox://mapbox.satellite' &&
              el.source != 'mapbox' &&
              el.source != 'composite'
            )
          })

          newStyle.layers = [...newStyle.layers, ...appLayers]

          this.map.setStyle(newStyle)
        })
    })

    hook.handleTargetedEvent(
      'debug/add-points',
      ({ name, points, color, radius }) => {
        const features = points.map((point) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: point
          }
        }))

        this.map.addSource(`debug/stands-points_${name}`, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: features
          }
        })

        this.map.addLayer({
          id: `debug/stands-points_${name}`,
          type: 'circle',
          source: `debug/stands-points_${name}`,
          paint: {
            'circle-radius': radius,
            'circle-color': color,
            'circle-opacity': 0.4
          }
        })
      }
    )

    hook.handleTargetedEvent('debug/remove-points', ({ name }) => {
      map.removeLayer(`debug/stands-points_${name}`)
      map.removeSource(`debug/stands-points_${name}`)
    })

    hook.handleTargetedEvent(
      'debug/show-stands-boundary',
      ({ source, debugTarget }) => {
        if (!this.map.getLayer('debug/stands-boundary')) {
          this.map.addLayer({
            id: 'debug/stands-boundary',
            type: 'line',
            source: source,
            'source-layer': 'stand',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
              visibility: 'visible'
            },
            paint: {
              'line-color': '#FAF0E6',
              'line-opacity': 0.5,
              'line-width': 1
            }
          })
          this.map.on('click', (e) => {
            hook.sendTargetedEvent(debugTarget, 'debug/map.click', e.lngLat)
          })
        }
      }
    )

    hook.handleTargetedEvent('debug/remove-stands-boundary', ({ name }) => {
      if (map.getLayer('debug/stands-boundary'))
        map.removeLayer('debug/stands-boundary')
    })
  }

  loaded() {
    this.setupInvisibleCategoryLayers()
  }

  setupInvisibleCategoryLayers() {
    this.map.addSource('categorySource', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [-41.28666552, 174.772996908] // Wellington, NZ
        }
      }
    })

    const categoryLayers = [
      'bottom',
      'disturbance',
      'treatment',
      'ownership',
      'parcels',
      'sara-geometry',
      'objective',
      'management',
      'sara',
      'boundary',
      'selected-boundary',
      'drawing',
      'top'
    ]

    categoryLayers.forEach((category) => {
      this.map.addLayer({
        id: category,
        layout: { visibility: 'none' },
        paint: { 'fill-color': 'transparent' },
        source: 'categorySource',
        type: 'fill'
      })
    })
  }

  setSource({ id, ...config }) {
    const source = this.map.getSource(id)
    if (!source) {
      this.map.addSource(id, config)
      this.loadedSources[id] = this.loadedSources[id] || false
      return
    }
    if (config.data && source.data !== config.data) {
      source.setData(config.data)
    } else if (config.url && source.url !== config.url) {
      source.setUrl(config.url)
    }
  }

  setLayer(config, category) {
    // Add a new layer, but only if it's not hidden.
    if (!this.map.getLayer(config.id) && config.layout?.visibility !== false) {
      this.map.addLayer(config, category)
    } else {
      // Update an existing layer:
      // Update paint properties with new values.
      Object.entries(config.paint || {}).forEach(([key, val]) =>
        this.map.setPaintProperty(config.id, key, val)
      )
      // Update layout properties with new values.
      Object.entries(config.layout || {}).forEach(([key, val]) =>
        this.map.setLayoutProperty(config.id, key, val)
      )
    }
  }
}

// if the passed in data is not an array return it wrapped in an array
// return an empty array if no data is passed in
const ensureArray = (data) => {
  if (data === undefined || data === null) return []
  if (Array.isArray(data)) return data
  return [data]
}
